import autobind from 'core-decorators/lib/autobind';
import Vidi from 'vidi';

import { WixConnect } from 'widget/player-widget/utils/wix-connect';

import { pickContextTypes } from '../../context-types';


const EVENTS = [
  'onAbort',
  'onCanPlay',
  'onCanPlayThrough',
  'onDurationChange',
  'onEmptied',
  'onEncrypted',
  'onEnded',
  'onError',
  'onLoadedData',
  'onLoadedMetadata',
  'onLoadStart',
  'onPause',
  'onPlay',
  'onPlaying',
  'onProgress',
  'onRateChange',
  'onSeeked',
  'onSeeking',
  'onStalled',
  'onSuspend',
  'onTimeUpdate',
  'onVolumeChange',
  'onWaiting',
];


@WixConnect()
export default class InternalPlayer extends React.Component {

  static contextTypes = pickContextTypes(
    'muted',
    'loop',
    'volume',
    'duration',
    'currentTime',
    'userTime',
    'controls',
    'paused',
    'resetSeek',
    'updateStateFromVideo',
    'togglePause',
    'toggleError',
    'toggleBeenPlayed',
    'toggleEnded',
  );

  static propTypes = {
    onError: React.PropTypes.func,
    className: React.PropTypes.string,
    videoUrls: React.PropTypes.object,
    refReceived: React.PropTypes.func,
  };

  static defaultProps = {
    refReceived: _.noop,
  };

  componentWillMount() {
    // Also bind 'this' as we call _updateStateFromVideo outside
    // of Reacts synthetic events as well.
    this._updateStateFromVideo = _.throttle(this.updateStateFromVideo, 100).bind(this);
    // Set up all React media events and call method
    // on props if provided.
    this.mediaEventProps = _.reduce(EVENTS, (p, c) => {
      p[c] = () => {
        if (c in this.props && _.isFunction(this.props[c])) {
          // A prop exists for this mediaEvent, call it.
          this.props[c]();
        }
        this._updateStateFromVideo();
      };

      return p;
    }, {});
  }

  componentDidMount() {
    // Add error event listener to video.
    this.vidi.on('error', this.handleVideoError);
    this.videoEl.addEventListener('ended', this.handleVideoEnd, true);
    this.videoEl.addEventListener('play', this.handleVideoPlay, true);
    this.videoEl.addEventListener('pause', this.handleVideoPause, false);
    this.videoEl.addEventListener('canplay', this.autoPlay, true);

    // to display loaded. Playing will started on canplay event
    const { paused } = this.context;
    if (!paused) {
      this.togglePause(paused);
    }
  }

  componentWillUpdate(nextProps, nextState, nextContext) {
    const {
      videoUrls: nextVideoUrls,
    } = nextProps;

    const {
      paused: nextPaused,
    } = nextContext;

    const { videoUrls } = this.props;
    const { paused, volume, userTime, resetSeek } = this.context;

    this.setVolume(volume);

    if (!_.isEqual(nextVideoUrls, videoUrls)) {
      return;
    }

    if (_.isNumber(userTime) && userTime >= 0) {
      this.seek(userTime);
      resetSeek();
    }


    if (nextPaused !== paused) {
      this.togglePause(nextPaused);
    }
  }

  componentDidUpdate(prevProps) {
    const { videoUrls } = this.props;

    if (!_.isEqual(prevProps.videoUrls, videoUrls)) {
      this.setSrc(videoUrls);
    }
  }

  /**
   * Removes event listeners bound outside of React's synthetic events
   * @return {undefined}
   */
  componentWillUnmount() {
    // Remove event listener from video.
    this.vidi.off('error', this.handleVideoError);
    this.videoEl.removeEventListener('ended', this.handleVideoEnd, true);
    this.videoEl.removeEventListener('play', this.handleVideoPlay, true);
    this.videoEl.removeEventListener('pause', this.handleVideoPause, false);
    this.videoEl.removeEventListener('canplay', this.autoPlay, true);
  }

  vidi = new Vidi();

  @autobind
  handleVideoError(error) {
    this.props.onError(error);

    if (!this.videoEl) {
      return;
    }

    const { networkState, NETWORK_NO_SOURCE } = this.videoEl;

    if (networkState === NETWORK_NO_SOURCE && _.size(this.vidi.src) > 0) {
      this.context.toggleError(true);
    }
  }

  @autobind
  handleVideoEnd() {
    const { toggleEnded, togglePause } = this.context;

    togglePause(true);
    toggleEnded(true);
  }

  @autobind
  handleVideoPlay() {
    this.context.toggleEnded(false);
  }

  // fix. Vidi can`t recognize just list of urls with GET params
  toVidiTypes(url, type) {
    if (/mp4/i.test(type)) {
      return { url, type: Vidi.MediaStreamTypes.MP4, name: /[0-9]+p/i.exec(type)[0] };
    }
    if (/mpd/i.test(type)) {
      return { url, type: Vidi.MediaStreamTypes.DASH };
    }
    if (/m3u8/i.test(type)) {
      return { url, type: Vidi.MediaStreamTypes.HLS };
    }
    if (/webm/i.test(type)) {
      return { url, type: Vidi.MediaStreamTypes.WEBM };
    }
    return null;
  }

  setSrc(urlsByVodTypes) {
    const vidiUrls = _.map(urlsByVodTypes, this.toVidiTypes);
    this.vidi.src = _.compact(vidiUrls);
  }

  @autobind
  handleVideoPause() {
    this.context.togglePause(true);
  }

  /**
   * Sets the video to fullscreen.
   * @return {undefined}
   */
  @autobind
  goFullScreen() {
    if (this.videoEl.requestFullscreen) {
      this.videoEl.requestFullscreen();
    } else if (this.videoEl.msRequestFullscreen) {
      this.videoEl.msRequestFullscreen();
    } else if (this.videoEl.mozRequestFullScreen) {
      this.videoEl.mozRequestFullScreen();
    } else if (this.videoEl.webkitRequestFullscreen) {
      this.videoEl.webkitRequestFullscreen();
    }
  }

  /**
   * Plays the video if paused == false was set.
   * @return {undefined}
   */
  @autobind
  autoPlay() {
    const { paused } = this.context;

    if (!paused) {
      if (this.videoEl.paused) {
        this.videoEl.play();
      }
    }
  }

  /**
   * Plays the video.
   * @return {undefined}
   */
  @autobind
  play() {
    this.context.toggleBeenPlayed(true);

    this.videoEl.play();
  }

  /**
   * Pauses the video.
   * @return {undefined}
   */
  @autobind
  pause() {
    this.videoEl.pause();
  }

  /**
   * Toggles the video to play and pause.
   * @return {undefined}
   */
  @autobind
  togglePause(paused) {
    if (paused) {
      this.pause();
    } else {
      this.play();
    }
  }

  /**
   * Seeks the video timeline.
   * @param  {number} time The value in seconds to seek to
   * @return {undefined}
   */
  @autobind
  seek(time) {
    this.videoEl.currentTime = time;
  }

  /**
   * Sets the video volume.
   * @param  {number} volume The volume level between 0 and 1.
   * @return {undefined}
   */
  @autobind
  setVolume(volume) {
    if (!this.videoEl) {
      return;
    }

    this.videoEl.volume = volume;
  }

  /**
   * Updates the React component state from the DOM video properties.
   * This is where the magic happens.
   * @return {undefined}
   */
  @autobind
  updateStateFromVideo() {
    if (!this.videoEl) {
      return;
    }

    const { updateStateFromVideo } = this.context;

    const duration = this.videoEl.duration || 0;
    const bufferedLength = this.videoEl.buffered.length;

    updateStateFromVideo({
      // Standard video properties
      duration,
      currentTime: this.videoEl.currentTime,
      buffered: this.videoEl.buffered,

      readyState: this.videoEl.readyState,

      // Non-standard state computed from properties
      percentageBuffered: bufferedLength && this.videoEl.buffered.end(bufferedLength - 1) / duration * 100,
      percentagePlayed: this.videoEl.currentTime / duration * 100,
      loading: this.videoEl.readyState < this.videoEl.HAVE_ENOUGH_DATA,
    });
  }

  @autobind
  handleRef(element) {
    this.props.refReceived(element);

    this.videoEl = element;
    this.vidi.setVideoElement(element);
    if (element) {
      this.setSrc(this.props.videoUrls);
    }
  }


  render() {
    const { className } = this.props;
    const { muted, loop } = this.context;

    const props = {
      className,
      muted,
      loop,
    };

    return (
      <video {...props}
             {...this.mediaEventProps}
             ref={this.handleRef} />
    );
  }
}



// WEBPACK FOOTER //
// ./src/widget/player-widget/components/player/video/internal/internal.js